// Registo rápido de rendimento — alinhado com `.pen` (Novo rendimento).
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { Button } from '../ui/Button';
import { IncomeSourceFormDialog } from './IncomeSourceFormDialog';
import {
  mergeSourceIntoList,
  sourceIdsMatch,
  suggestedAmountForSource,
} from './incomeSourcePickerUtils';
import { QuickCreateDateChips } from '../quickCreate/QuickCreateDateChips';
import { QuickCreateField } from '../quickCreate/QuickCreateField';
import { QuickCreateFieldGroup } from '../quickCreate/QuickCreateFieldGroup';
import { QuickCreateFormSkeleton } from '../quickCreate/QuickCreateFormSkeleton';
import { QuickCreateHeroAmount } from '../quickCreate/QuickCreateHeroAmount';
import { QuickCreateInlineCreateField } from '../quickCreate/QuickCreateInlineCreateField';
import { QuickCreatePickerFooter } from '../quickCreate/QuickCreatePickerFooter';
import { QuickCreatePickerRow } from '../quickCreate/QuickCreatePickerRow';
import {
  DatePickerSheet,
  SourcePickerSheet,
  recurrenceLabel,
} from '../quickCreate/QuickCreatePickers';
import { WizardErrorCallout } from '../wizard/WizardErrorCallout';
import pt from '../../locales/pt';
import { parsePtNumber } from '../../utils/money';
import { pickNextIncomeSourceColor } from '../../utils/incomeSourceColors';
import { defaultIconNameForCategory, resolveIncomeSourceIcon } from '../../utils/incomeSourceIcons';
import { clampISODate, todayISO } from '../../utils/quickCreateDates';
import {
  useCreateIncomeRecordMutation,
  useCreateIncomeSourceMutation,
  useIncomeSourcesQuery,
  useUpdateIncomeRecordMutation,
} from '../../features/income/queries';
import { useGuardedBottomSheetClose } from '../../hooks/useGuardedBottomSheetClose';

const TITLE_CLASS_NAME = 'type-title-page text-card-foreground';

export function QuickIncomeSheet({ isOpen, onClose, cycle, editing = null }) {
  const isEdit = Boolean(editing?.id);
  const {
    data: sources = [],
    isLoading,
    isError,
    refetch,
  } = useIncomeSourcesQuery({ enabled: isOpen });
  const emptyMutation = { mutateAsync: async () => {}, isPending: false };
  const createRecord = useCreateIncomeRecordMutation() ?? emptyMutation;
  const updateRecord = useUpdateIncomeRecordMutation() ?? emptyMutation;
  const createSource = useCreateIncomeSourceMutation() ?? emptyMutation;

  const activeSources = useMemo(
    () => (Array.isArray(sources) ? sources.filter((s) => s.is_active !== false) : []),
    [sources],
  );

  const minDate = cycle?.start;
  const maxDate = cycle?.end || todayISO();

  const [sourceId, setSourceId] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [sourceFormOpen, setSourceFormOpen] = useState(false);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [pinnedSource, setPinnedSource] = useState(null);
  const [firstSourceExpanded, setFirstSourceExpanded] = useState(false);
  const [firstSourceName, setFirstSourceName] = useState('');
  const [firstSourceError, setFirstSourceError] = useState(null);

  const openedRef = useRef(false);

  const needsFirstSource =
    !isEdit && activeSources.length === 0 && !pinnedSource;

  useEffect(() => {
    if (!isOpen) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;

    setError(null);
    setSourceFormOpen(false);
    setSourcePickerOpen(false);
    setFirstSourceExpanded(false);
    setFirstSourceName('');
    setFirstSourceError(null);
    if (isEdit) {
      setSourceId(editing.source);
      setDate(editing.received_on || todayISO());
      setAmount(String(editing.amount).replace('.', ','));
      setNotes(editing.notes || '');
      setPinnedSource(null);
      return;
    }
    const defaultDate = clampISODate(todayISO(), minDate, maxDate);
    setDate(defaultDate);
    const first = activeSources[0] ?? null;
    setSourceId(first?.id ?? null);
    setAmount(suggestedAmountForSource(first));
    setNotes('');
    setPinnedSource(null);
  }, [isOpen, isEdit, editing, activeSources, minDate, maxDate]);

  const gridSources = useMemo(() => {
    let list = [...activeSources];
    if (pinnedSource && !list.some((s) => sourceIdsMatch(s.id, pinnedSource.id))) {
      list = mergeSourceIntoList(list, pinnedSource);
    }
    return list;
  }, [activeSources, pinnedSource]);

  const SOURCE_LIST_CAP = 3;
  const orderedSources = useMemo(() => {
    const idx = gridSources.findIndex((s) => sourceIdsMatch(s.id, sourceId));
    if (idx <= 0) return gridSources;
    const copy = [...gridSources];
    const [selected] = copy.splice(idx, 1);
    copy.unshift(selected);
    return copy;
  }, [gridSources, sourceId]);
  const sourcePreview = orderedSources.slice(0, SOURCE_LIST_CAP);
  const sourceExtra = orderedSources.slice(SOURCE_LIST_CAP);

  const numeric = parsePtNumber(amount);
  const isPending = createRecord.isPending || updateRecord.isPending;
  const canSubmit = sourceId && numeric && numeric > 0 && date && !isPending;
  const hasSources = activeSources.length > 0 || pinnedSource;

  const handleCreateSource = async (payload) => {
    const created = await createSource.mutateAsync(payload);
    setPinnedSource(created);
    setSourceId(created.id);
    setAmount(suggestedAmountForSource(created));
    setSourceFormOpen(false);
    setFirstSourceExpanded(false);
    setFirstSourceName('');
    setFirstSourceError(null);
    await refetch();
    toast.success(pt.income.sources.form.submitCreate);
    return created;
  };

  const handleInlineCreateSource = async () => {
    if (!firstSourceName.trim() || createSource.isPending) return;
    setFirstSourceError(null);
    try {
      await handleCreateSource({
        name: firstSourceName.trim(),
        category: 'salary',
        recurrence: 'monthly_fixed',
        base_amount: null,
        notes: '',
        color: pickNextIncomeSourceColor(activeSources),
        icon: defaultIconNameForCategory('salary'),
      });
    } catch (err) {
      setFirstSourceError(
        typeof err === 'string'
          ? err
          : err?.detail || err?.name || pt.auth.errors.generic,
      );
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    const receivedOn = clampISODate(date, minDate, maxDate);
    const payload = {
      source: sourceId,
      received_on: receivedOn,
      amount: numeric.toFixed(2),
      notes,
    };
    try {
      if (isEdit) {
        await updateRecord.mutateAsync({ id: editing.id, ...payload });
        toast.success(pt.income.quickIncome.updatedToast);
      } else {
        await createRecord.mutateAsync(payload);
        toast.success(pt.income.quickIncome.successToast);
      }
      onClose?.();
    } catch (err) {
      const msg =
        (err && (err.detail || err.message || JSON.stringify(err))) ||
        'Erro inesperado.';
      setError(msg);
    }
  };

  const title = isEdit ? pt.income.quickIncome.editTitle : pt.quickCreate.income.title;

  const { handleOpenChange, blockDismissProps, wrapChildOpenChange } = useGuardedBottomSheetClose({
    onClose: () => onClose?.(),
    childOpen: [sourcePickerOpen, sourceFormOpen, datePickerOpen],
  });

  return (
    <>
      <BottomSheet open={isOpen} onOpenChange={handleOpenChange}>
        <BottomSheetContent
          title={title}
          layout="bottom"
          headerAlign="start"
          titleClassName={TITLE_CLASS_NAME}
          {...blockDismissProps}
          footer={
            !isLoading && !isError && hasSources ? (
              <Button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                loading={isPending}
                className="w-full justify-center"
              >
                {isPending ? pt.common.saving : pt.quickCreate.income.submit}
              </Button>
            ) : null
          }
        >
          {isLoading ? (
            <QuickCreateFormSkeleton />
          ) : isError ? (
            <ErrorRetryPanel refetch={refetch} />
          ) : (
            <div className="flex flex-col gap-space-4 pb-2">
              <QuickCreateHeroAmount
                label={pt.quickCreate.income.amountLabel}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />

              <QuickCreateField label={pt.quickCreate.sourceLabel}>
                {needsFirstSource ? (
                  <QuickCreateInlineCreateField
                    expanded={firstSourceExpanded}
                    onExpand={() => setFirstSourceExpanded(true)}
                    onCancel={() => setFirstSourceExpanded(false)}
                    title={pt.quickCreate.createFirstSource}
                    description={pt.quickCreate.createFirstSourceDescription}
                    nameLabel={pt.income.sources.form.nameLabel}
                    name={firstSourceName}
                    onNameChange={(event) => setFirstSourceName(event.target.value)}
                    namePlaceholder={pt.income.sources.form.namePlaceholder}
                    onCreate={handleInlineCreateSource}
                    submitLabel={pt.quickCreate.createFirstSourceInlineSubmit}
                    cancelLabel={pt.quickCreate.createFirstSourceCancel}
                    isPending={createSource.isPending}
                    error={firstSourceError}
                  />
                ) : (
                  <div className="flex flex-col gap-space-2">
                    {sourcePreview.map((source) => (
                      <QuickCreatePickerRow
                        key={source.id}
                        icon={resolveIncomeSourceIcon(source)}
                        name={source.name}
                        subtitle={recurrenceLabel(source.recurrence)}
                        selected={sourceIdsMatch(source.id, sourceId)}
                        onSelect={() => {
                          setSourceId(source.id);
                          setAmount(suggestedAmountForSource(source));
                        }}
                      />
                    ))}
                    <QuickCreatePickerFooter
                      onAdd={() => setSourceFormOpen(true)}
                      addLabel={pt.quickCreate.newSourceShort}
                      addAriaLabel={pt.quickCreate.newSource}
                      onShowMore={sourceExtra.length > 0 ? () => setSourcePickerOpen(true) : undefined}
                      moreCount={sourceExtra.length}
                      moreLabel={pt.quickCreate.moreSourcesShort}
                    />
                  </div>
                )}
              </QuickCreateField>

              <QuickCreateDateChips
                value={date}
                onSelect={setDate}
                onOpenPicker={() => setDatePickerOpen(true)}
                min={minDate}
                max={maxDate}
                ariaLabel={pt.quickCreate.dateLabel}
              />

              {!needsFirstSource ? (
                <QuickCreateFieldGroup
                  label={pt.quickCreate.noteLabel}
                  optional={pt.quickCreate.optional}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder={pt.quickCreate.notePlaceholder}
                />
              ) : null}

              {error ? (
                <WizardErrorCallout>
                  {typeof error === 'string' ? error : JSON.stringify(error)}
                </WizardErrorCallout>
              ) : null}
            </div>
          )}
        </BottomSheetContent>
      </BottomSheet>

      <IncomeSourceFormDialog
        isOpen={sourceFormOpen}
        onClose={() => setSourceFormOpen(false)}
        onSubmitExternal={handleCreateSource}
        isPendingExternal={createSource.isPending}
      />

      <SourcePickerSheet
        open={sourcePickerOpen}
        onOpenChange={wrapChildOpenChange(setSourcePickerOpen)}
        sources={sourceExtra}
        selectedId={sourceId}
        onSelect={(id) => {
          setSourceId(id);
          const source = gridSources.find((item) => sourceIdsMatch(item.id, id));
          setAmount(suggestedAmountForSource(source));
        }}
        onAddNew={() => setSourceFormOpen(true)}
      />

      <DatePickerSheet
        open={datePickerOpen}
        onOpenChange={wrapChildOpenChange(setDatePickerOpen)}
        value={date}
        min={minDate}
        max={maxDate}
        onSelect={setDate}
      />
    </>
  );
}

function ErrorRetryPanel({ refetch }) {
  return (
    <div className="flex flex-col items-center gap-space-3 py-6 text-center">
      <p className="type-body-sm text-muted-foreground">{pt.auth.errors.generic}</p>
      <Button type="button" variant="secondary" onClick={() => refetch()}>
        {pt.common.retry}
      </Button>
    </div>
  );
}

export default QuickIncomeSheet;
